import { useCallback, useMemo, useRef, useState } from 'react';
import { Campus, CampusFacility } from 'api/campus';
import { loadCampusFacilityIds, saveCampusFacilityIds } from 'utils/facilityStorage';

interface CampusChoice {
  campusUuid: string | null;
  ids: string[];
}

/**
 * Facilities a roll-up view shows: all by default, remembered per campus, stale ids dropped.
 * Worked out during render rather than synced in an effect, so the selection
 * always belongs to the campus passed in the same render.
 */
export function useCampusFacilitySelection(
  campus: Campus | null,
  campusFacilities: CampusFacility[],
  options: { startWithAll?: boolean } = {}
): [string[], (ids: string[]) => void] {
  const allIds = campusFacilities.map((f) => String(f.id));
  const allKey = allIds.join(',');
  const campusUuid = campus?.uuid || null;
  // `startWithAll` (e.g. a dashboard deep link) resets the remembered choice to all, once.
  const startWithAll = useRef(!!options.startWithAll);
  // What the person picked on this page; another campus's pick doesn't apply.
  const [choice, setChoice] = useState<CampusChoice | null>(null);

  const selected = useMemo((): string[] => {
    if (!campusUuid) return allIds;
    if (startWithAll.current && allIds.length > 0) {
      startWithAll.current = false;
      saveCampusFacilityIds(campusUuid, allIds);
      return allIds;
    }
    const picked =
      choice?.campusUuid === campusUuid ? choice.ids : loadCampusFacilityIds(campusUuid);
    const valid = (picked || []).filter((id) => allIds.includes(id));
    return valid.length > 0 ? valid : allIds;
    // allKey stands in for allIds, which is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusUuid, allKey, choice]);

  const update = useCallback(
    (ids: string[]) => {
      setChoice({ campusUuid, ids });
      if (campusUuid) saveCampusFacilityIds(campusUuid, ids);
    },
    [campusUuid]
  );

  return [selected, update];
}
